import { z } from 'zod'

import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  DealPosition,
  DeliveryHourType,
  DeliveryType,
} from '@enechain/eposition-proto/types/v1/deal_pb'

import { createTransactionSchema, spreadTradeSchema } from '../../../../schemas'
import {
  getSelectedOrderIndexes,
  validateBaseTransactions,
  validateExchangeTransactions,
} from './validator'

const looseTransactionSchema = createTransactionSchema()
  .extend({
    areaCode: z.nativeEnum(AreaCode),
    deliveryDateRange: z.object({
      deliveryStartDate: z.date().optional(),
      deliveryEndDate: z.date().optional(),
    }),
    unitPricePerKwh: z.string(),
    volumeInKw: z.string(),
    deliveryHourType: z.nativeEnum(DeliveryHourType),
  })
  .partial({
    unitPricePerKwh: true,
    volumeInKw: true,
  })

const orderPatternSchema = z.object({
  isPhysicalFuture: z.boolean(),
  selfDeliveryType: z.nativeEnum(DeliveryType),
  eClearDeliveryType: z.nativeEnum(DeliveryType),
  dealPosition: z.nativeEnum(DealPosition),
  spreadType: spreadTradeSchema,
  baseSummaryOrder: looseTransactionSchema,
  exchangeSummaryOrder: looseTransactionSchema.optional(),
  baseTransactions: z.array(looseTransactionSchema).optional(),
  exchangeTransactions: z.array(looseTransactionSchema).optional(),
})

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createValidatedOrderPatternSchema(
  allowFractionalVolume: boolean,
) {
  const strictTx = createTransactionSchema({ allowFractionalVolume })
  return orderPatternSchema.extend({
    baseSummaryOrder: strictTx,
    exchangeSummaryOrder: strictTx.optional(),
    baseTransactions: z.array(strictTx).optional(),
    exchangeTransactions: z.array(strictTx).optional(),
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createSimulationSchema() {
  return z
    .object({
      marginSimulationRelation: z.array(
        z.object({ values: z.array(z.number()) }),
      ),
      availableDealSimulationRelation: z.array(z.number()),
      orderPattern: z.array(orderPatternSchema),
    })
    .superRefine((value, ctx) => {
      const selectedOrderIndexes = getSelectedOrderIndexes(
        value.availableDealSimulationRelation,
        value.marginSimulationRelation.map((relation) => relation.values),
      )

      for (const [index, order] of value.orderPattern.entries()) {
        // シミュレーションのrelationに含まれている注文のみバリデーションを行う
        if (!selectedOrderIndexes.has(index)) {
          continue
        }

        // 必須チェックなど、portalの初期入力用にゆるくしていたバリデーションを実行
        const result = createValidatedOrderPatternSchema(
          !order.isPhysicalFuture,
        ).safeParse(order)
        if (!result.success) {
          for (const err of result.error.errors) {
            ctx.addIssue({
              ...err,
              path: ['orderPattern', index, ...err.path],
            })
          }
        }

        validateBaseTransactions(order, index, ctx)
        validateExchangeTransactions(order, index, ctx)
      }
    })
}

// 後方互換 export（既定 1MW）
export const validatedOrderPatternSchema =
  createValidatedOrderPatternSchema(false)
export const simulationSchema = createSimulationSchema()

export type LooseTransactionSchema = z.input<typeof looseTransactionSchema>
export type TransactionSchema = z.input<
  ReturnType<typeof createTransactionSchema>
>
export type OrderPatternSchema = z.input<typeof orderPatternSchema>
export type OrderSimulationSchema = z.input<typeof simulationSchema>
export type ValidatedOrderPatternSchema = z.input<
  typeof validatedOrderPatternSchema
>
